/**
 * Le fond d'une surface sélectionnée — carte de dossier, ligne de fichier, ligne
 * d'un arbre de destination — en UN seul endroit, pour les deux navigateurs
 * (`folders/` et `folder-browser/`).
 *
 * POURQUOI une constante partagée : ces surfaces portaient chacune leur propre
 * littéral `bg-accent-blue/10`, et la barre d'actions un troisième. Trois copies
 * de la même règle dérivent au premier ajustement, et la sélection est
 * exactement le genre de signal qui doit se lire pareil partout.
 *
 * LA COULEUR PORTE LE SIGNAL, ET ELLE SEULE. Ni filet, ni rail, ni pastille, ni
 * ombre : un état aussi banal que « sélectionné » n'a pas à dessiner de trait
 * autour de son objet (arbitrage du 2026-09-16, après un essai avec bordure et
 * rail gauche, jugés trop appuyés).
 *
 * POURQUOI un bleu ÉCRIT EN CLAIR plutôt qu'un lavis d'`--accent-blue` : ce
 * token est un navy très sombre (`hsl(220 100% 25%)` ≈ #002A80), fait pour du
 * texte et des liens. Dilué, il ne donne pas un bleu clair mais un gris
 * lavande — #E6EBF2 à 10 %, #F1F2F7 à 6 % : à faible opacité on ne voit rien, à
 * forte opacité on voit du gris sale. Aucune valeur intermédiaire n'est un bleu.
 * D'où une teinte claire et franche (214°, saturée), posée à faible opacité :
 * #E0EDFD se lit BLEU, donc « sélectionné », sans peser sur la page. Même
 * raisonnement, et mêmes valeurs explicites, que pour `FolderGlyph`.
 */
export const SELECTION_SURFACE = "bg-[hsl(214_92%_60%_/_0.16)]";

/**
 * Le MÊME fond, mais opaque : `SELECTION_SURFACE` aplati sur blanc.
 *
 * Réservé aux surfaces qui doivent masquer ce qui défile dessous — aujourd'hui
 * la seule bande d'actions de sélection, qui est `sticky`. Avec le lavis
 * translucide, le contenu de la page se lirait au travers ; avec du blanc, la
 * bande n'appartiendrait pas à la sélection qu'elle annonce.
 *
 * ⚠️ Les deux valeurs vont ENSEMBLE : #E0EDFD est exactement `SELECTION_SURFACE`
 * aplati sur blanc. Changer l'une sans l'autre ferait diverger la bande des
 * cartes qu'elle surmonte — d'où leur cohabitation ici.
 */
export const SELECTION_SURFACE_OPAQUE = "bg-[#E0EDFD]";

/**
 * Le survol de TOUT bouton d'action de la grille : le ⋮ d'une carte de dossier
 * ou d'une ligne de pièce, ses voisins (aperçu, ouvrir dans Word), et les
 * boutons de la bande de sélection (télécharger, déplacer, tamponner,
 * « Tout sélectionner », « Annuler »).
 *
 * UNE SEULE couleur de survol pour tous, quelle que soit la surface (2026-09-16).
 * Le réglage a d'abord été conditionnel — bleu sur une ligne sélectionnée, gris
 * ailleurs — mais deux boutons voisins s'allumaient alors de deux façons selon
 * l'état de leur ligne, ce qui se lisait comme deux familles de boutons plutôt
 * que comme un état.
 *
 * SEULE EXCEPTION, volontaire : « Supprimer » garde un survol rouge
 * (`destructive`). Une action irréversible ne se confond pas avec les autres.
 *
 * L'écart avec `SELECTION_SURFACE` se règle SERRÉ : 0,16 → 0,24. Avec une
 * teinte saturée, un écart de 0,14 (l'essai à 0,30) donne déjà une pastille
 * nettement plus foncée que la surface, qui saute aux yeux au moindre passage
 * de souris. Le survol doit se sentir, pas s'annoncer.
 */
export const ACTION_HOVER = "hover:bg-[hsl(214_92%_60%_/_0.24)]";
